import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import {
  Cluster,
  Preview,
} from "../../views/pages/DataPage/CardsView/DataList";
import { useLocation, useNavigate } from "react-router";
import { Option } from "../../views/uikit/FormFields/SelectList";
import { toast } from "react-toastify";
import VLToastComponent from "../../views/components/VLToastComponent";
import {
  ActionConfig,
  PreviewData,
  ReviewData,
  ReviewResult,
  ActionType,
  TooltipId,
} from "views/pages/DataPage/CardsView/ReviewList/types";
import {
  actionListConfigs,
  getBorderColorByResult,
  getOpacityByResult,
  getTooltipText,
} from "views/pages/DataPage/CardsView/ReviewList/utils";
import { LABEL_CLASS_PARAMETER } from "../../helpers/constants/miscellaneous";

export const useReviews = (
  onReviewAction: (sampleId?: string) => void,
  datasetId?: string,
  flowId?: string | null,
  labelId?: string | null,
  exportData?: any,
  clearSelectedMedia?: () => void,
) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [reviews, setReviews] = useState<Cluster[] | null>(null);
  const [selectedReview, setSelectedReview] = useState<Cluster | null>(null);
  const [error, setError] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [labelsOptions, setLabelsOptions] = useState<Option[]>([]);
  const [isAllReviewed, setIsAllReviewed] = useState(false);

  const isInferred = (result: ReviewResult): boolean => result === "INFERRED";

  // Helper function to update selected review state
  const updateSelectedReviewState = useCallback(
    (
      sampleIds: string[],
      responseData: PreviewData,
      returnedLabelIds: string[],
    ) => {
      let removedIndexs: number[] = [];

      setSelectedReview((prev) => {
        if (!prev) return prev;

        if (
          returnedLabelIds.length > 0 &&
          !returnedLabelIds.includes(prev.id)
        ) {
          removedIndexs = sampleIds
            .map((sampleId) => {
              return prev.previews.findIndex((p) => p.media_id === sampleId);
            })
            .filter((index) => index !== -1);

          return {
            ...prev,
            clusterLabel: responseData.label_category_id,
            previews: prev.previews.filter(
              (p) => !sampleIds.includes(p.media_id),
            ),
          };
        }

        return {
          ...prev,
          clusterLabel: responseData.label_category_id,
          previews: prev.previews.map((p) =>
            sampleIds.includes(p.media_id)
              ? {
                  ...p,
                  borderColor: getBorderColorByResult[responseData.result],
                  opacity: getOpacityByResult[responseData.result],
                  result: responseData.result,
                  floatingDetails: !responseData.result
                    ? "For Review"
                    : "Reviewed",
                  label: {
                    name: responseData.label || prev.label.name,
                    type: "User",
                  },
                }
              : p,
          ),
        };
      });

      return removedIndexs;
    },
    [setSelectedReview],
  );

  // Update a single review
  const updateReview = useCallback(
    async (
      sampleId: string,
      result: ReviewResult,
      labelCategoryId?: string,
    ) => {
      if (!datasetId || !flowId) return;

      const params = new URLSearchParams();
      params.set("review_result", result);
      if (labelCategoryId) {
        params.set("label_category_id", labelCategoryId);
      }

      try {
        const url = composeApiURL(
          process.env.REACT_APP_API_ENDPOINT!,
          `/flywheel/${datasetId}/review/${flowId}/${sampleId}?${params.toString()}`,
        );

        const res = await axios.put<PreviewData>(url);

        const responseData = res.data;
        const returnedLabelIds = [res.data.label_category_id];

        onReviewAction(sampleId);
        const removedIndexs = updateSelectedReviewState(
          [sampleId],
          responseData,
          returnedLabelIds,
        );

        return { item: res.data, _removedIndexs: removedIndexs };
      } catch (err) {
        console.error("Error updating review", err);
        setError(err);
        toast(
          <VLToastComponent
            title="Review Action Failed"
            status="ERROR"
            description="Something went wrong. Please try again later."
          />,
        );
      }
    },
    [datasetId, flowId, onReviewAction, updateSelectedReviewState],
  );

  // Update multiple reviews
  const updateBulkReview = useCallback(
    async (
      sampleIds: string[],
      result: ReviewResult,
      labelCategoryId?: string,
    ) => {
      if (!datasetId || !flowId) return;

      const params = new URLSearchParams();
      params.set("review_result", result);
      if (labelCategoryId) {
        params.set("label_category_id", labelCategoryId);
      }

      try {
        const url = composeApiURL(
          process.env.REACT_APP_API_ENDPOINT!,
          `/flywheel/${datasetId}/review/${flowId}?${params.toString()}`,
        );
        const res = await axios.put<PreviewData[]>(url, {
          sample_ids: sampleIds,
        });

        const returnedLabelIds = res.data.map((item) => item.label_category_id);
        const responseData = res.data[0];

        onReviewAction(sampleIds[0]);
        const removedIndexs = updateSelectedReviewState(
          sampleIds,
          responseData,
          returnedLabelIds,
        );

        clearSelectedMedia?.();

        return { items: res.data, _removedIndexs: removedIndexs };
      } catch (err) {
        console.error("Error updating bulk review", err);
        setError(err);
        toast(
          <VLToastComponent
            title="Review Action Failed"
            status="ERROR"
            description="Something went wrong. Please try again later."
          />,
        );
      }
    },
    [
      datasetId,
      flowId,
      onReviewAction,
      updateSelectedReviewState,
      clearSelectedMedia,
    ],
  );

  const getSelectedIds = useCallback((): string[] => {
    if (!exportData) return [];
    return exportData
      ?.map((item: any) => item.media_id)
      .filter(Boolean) as string[];
  }, [exportData]);

  // Helper function to show confirmation toast
  const showConfirmationToast = useCallback(
    (
      onConfirm: () => Promise<
        | { item: PreviewData; _removedIndexs: number[] }
        | { items: PreviewData[]; _removedIndexs: number[] }
        | undefined
      >,
      actionType: ActionType,
    ) => {
      const doAction = async () => {
        const resObj: any = await onConfirm();
        if (!resObj) return;

        const isBulk = actionType === "BULK";
        const labelText = isBulk
          ? resObj.items?.[0]?.label
          : resObj.item?.label;
        const removedIndexs: number[] = resObj._removedIndexs || [];

        const toastId = toast(
          <VLToastComponent
            title={`Decline ${isBulk ? `Reviews` : `Review`}`}
            status={"ERROR"}
            description={`${isBulk ? "Images" : "Image"} moved to ${labelText}`}
            actionButton={{
              text: "Undo",
              type: "primary",
              onClick: async () => {
                const currentLabelId = new URLSearchParams(
                  window.location.search,
                ).get(LABEL_CLASS_PARAMETER);
                if (!currentLabelId) return;

                if (isBulk) {
                  const sampleIds = (resObj.items as PreviewData[]).map(
                    (r) => r.id,
                  );
                  const reverted: any = await updateBulkReview(
                    sampleIds,
                    "REVERT",
                    currentLabelId,
                  );
                  if (!reverted) return;

                  // Manually restore previews into current cluster in their previous positions
                  const restoredPreviews = (
                    reverted.items as PreviewData[]
                  ).map((r) =>
                    convertPreviewRef.current(r, labelsOptions, currentLabelId),
                  );

                  setSelectedReview((prev) => {
                    if (!prev) return prev;
                    const previews = [...prev.previews];
                    // Try to insert in recorded positions; fallback to front
                    restoredPreviews.forEach((rp, idx) => {
                      const insertIndex = removedIndexs[idx] ?? 0;
                      previews.splice(
                        Math.min(insertIndex, previews.length),
                        0,
                        rp,
                      );
                    });
                    return { ...prev, previews };
                  });
                } else {
                  const reverted = await updateReview(
                    (resObj.item as PreviewData).id,
                    "REVERT",
                    currentLabelId,
                  );
                  if (!reverted) return;

                  const restoredPreview = convertPreviewRef.current(
                    reverted.item as PreviewData,
                    labelsOptions,
                    currentLabelId,
                  );

                  setSelectedReview((prev) => {
                    if (!prev) return prev;
                    const previews = [...prev.previews];
                    const insertIndex = removedIndexs?.[0] ?? 0;
                    previews.splice(
                      Math.min(insertIndex, previews.length),
                      0,
                      restoredPreview,
                    );
                    return { ...prev, previews };
                  });
                }

                toast.dismiss(toastId);
              },
            }}
            onClose={() => toast.dismiss(toastId)}
          />,
          { autoClose: 7000, closeButton: false, closeOnClick: false },
        );
      };

      // Fire and forget; confirmation is handled within the toast
      void doAction();
    },
    [labelsOptions, setSelectedReview, updateBulkReview, updateReview],
  );

  // Helper function to create individual action items
  const createActionItem = useCallback(
    (
      actionConfig: ActionConfig,
      actionType: ActionType,
      id: string,
      currentLabelsOptions: Option[],
    ) => {
      const { text, icon, color, result, needsConfirmation, hasSubAction } =
        actionConfig;
      const isBulk = actionType === "BULK";

      const baseAction = {
        text,
        icon,
        type: "primary" as const,
        color,
        onClick: () => {
          if (needsConfirmation) {
            showConfirmationToast(
              () =>
                isBulk
                  ? updateBulkReview(getSelectedIds(), result)
                  : updateReview(id, result),
              actionType,
            );
          } else {
            isBulk
              ? updateBulkReview(getSelectedIds(), result)
              : updateReview(id, result);
          }
        },
      };

      const actionItem: any = {
        action: baseAction,
        tooltip: getTooltipText(actionType, result as TooltipId),
      };

      if (hasSubAction) {
        actionItem.action.onClick = () => {}; // Override for reassign
        actionItem.subAction = {
          options: currentLabelsOptions,
          onChange: (selectedIds: string[]) => {
            if (selectedIds.length) {
              isBulk
                ? updateBulkReview(
                    getSelectedIds(),
                    result,
                    selectedIds.toString(),
                  )
                : updateReview(id, result, selectedIds.toString());
            }
          },
        };
      }

      return actionItem;
    },
    [showConfirmationToast, getSelectedIds, updateBulkReview, updateReview],
  );

  const createActionList = useCallback(
    (actionType: ActionType, id: string, currentLabelsOptions: Option[]) => {
      return actionListConfigs.map((config) =>
        createActionItem(config, actionType, id, currentLabelsOptions),
      );
    },
    [createActionItem],
  );

  // 🔹 Ref to hold convertPreview
  const convertPreviewRef = useRef(
    (
      p: PreviewData,
      currentLabelsOptions: Option[],
      reviewLabel: string,
    ): Preview => ({
      media_id: p.id,
      image_id: p.id,
      relevanceType: null,
      media_uri: p.image_uri,
      type: "IMAGE" as const,
      title: p.image_uri,
      borderColor: p.result ? getBorderColorByResult[p.result] : undefined,
      opacity: p.result ? getOpacityByResult[p.result] : 100,
      displayIgnorLayout: p.result === "IGNORE",
      floatingDetails: !p.result
        ? "For Review"
        : isInferred(p.result)
          ? "Annotated"
          : "Reviewed",
      label: {
        name: p.label || reviewLabel,
        type: p.result ? (isInferred(p.result) ? "VL" : "User") : undefined,
      },
      actionList: createActionList("SINGLE", p.id, currentLabelsOptions),
    }),
  );

  useEffect(() => {
    setLoading(true);
    const fetchReviews = async () => {
      if (!datasetId || !flowId) return;
      try {
        const urlAll = composeApiURL(
          process.env.REACT_APP_API_ENDPOINT!,
          `/flywheel/${datasetId}/review/${flowId}`,
        );
        const resAll = await axios.get<ReviewData[]>(urlAll);

        const newLabelsOptions = resAll.data
          .filter((r) => r.label_category_id !== labelId)
          .map((r) => ({ label: r.label, value: r.label_category_id }));
        setLabelsOptions(newLabelsOptions);

        const transformReviewLocal = (review: ReviewData): Cluster => ({
          id: review.label_category_id,
          label: { name: review.label },
          cluster_id: review.label_category_id,
          title: review.total_count + " images",
          floatingDetails: review.pending_review_count + " For review",
          previews: review.previews.map((p) =>
            convertPreviewRef.current(p, newLabelsOptions, review.label),
          ),
          onDetailsClick: (data: Cluster) => {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set("labelId", data.id);
            navigate(`${location.pathname}?${searchParams.toString()}`);
          },
        });

        if (labelId) {
          const urlOne = composeApiURL(
            process.env.REACT_APP_API_ENDPOINT!,
            `/flywheel/${datasetId}/review/${flowId}/${labelId}`,
          );
          const resOne = await axios.get<ReviewData>(urlOne);
          setSelectedReview(transformReviewLocal(resOne.data));
        }

        const transformedReviews = resAll.data.map(transformReviewLocal);
        setReviews(transformedReviews);

        const allCompleted = resAll.data.every((cluster) =>
          cluster.previews.every((p) => !!p.result),
        );
        setIsAllReviewed(allCompleted);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [
    datasetId,
    flowId,
    labelId,
    navigate,
    location.pathname,
    location.search,
  ]);

  const bulkActionList = useMemo(() => {
    return createActionList("BULK", "", labelsOptions);
  }, [createActionList, labelsOptions]);

  return {
    reviews,
    selectedReview,
    error,
    loading,
    labelsOptions,
    isAllReviewed,
    bulkActionList,
  };
};
